"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { motion, useReducedMotion } from "framer-motion";
import {
  Wallet,
  LogIn,
  ShieldCheck,
  Lock,
  ScrollText,
  ChevronRight,
  Code2,
  BookOpen,
  Webhook,
  Bot,
  Terminal,
  Menu,
  X,
  ArrowLeft,
  ArrowRight,
  Check,
  Globe,
  Sparkles,
} from "lucide-react";
import {
  doc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useDispatch } from "react-redux";
import { setUser, clearUser } from "../slices/userSlices";

import { useAuth, useWallet } from "@crossmint/client-sdk-react-ui";

const GREEN = "#61CE70";

// Post-login deep link. ProtectedRoute stores the path a signed-out visitor
// asked for (webhook alert emails link straight to a delivery) under this
// key; we read it once and clear it so it can never outlive one login.
// Only in-app developer paths are honored: anything else (including a
// protocol-relative "//host" value) falls back to the dashboard, so the key
// can never be used to bounce a user off-site.
function consumeReturnTo(): string | null {
  try {
    const v = sessionStorage.getItem("cashtap:returnTo");
    sessionStorage.removeItem("cashtap:returnTo");
    return v && v.startsWith("/developers/") && !v.startsWith("//") ? v : null;
  } catch {
    // sessionStorage can throw (private mode, blocked site data).
    return null;
  }
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

/* Subtle green particle field — the same tasteful hero animation used on the
   marketing landing page. Slowly rotates with a gentle mouse parallax. */
function ParticleField() {
  const ref = useRef<THREE.Points>(null);
  const positions = useMemo(() => {
    const N = 1400;
    const arr = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 16;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return arr;
  }, []);

  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.y += delta * 0.015;
    // gentle mouse parallax
    const px = state.pointer.x * 0.18;
    const py = state.pointer.y * 0.12;
    ref.current.rotation.x += (py - ref.current.rotation.x) * 0.03;
    ref.current.position.x += (px - ref.current.position.x) * 0.03;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.028}
        color={GREEN}
        transparent
        opacity={0.55}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

function HeroCanvas() {
  return (
    <Canvas
      camera={{ position: [0, 0, 7], fov: 50 }}
      dpr={[1, 1.5]}
      gl={{ antialias: true, alpha: true }}
      style={{ pointerEvents: "none" }}
    >
      <ParticleField />
    </Canvas>
  );
}

// Ambient background — same visual language as the marketing landing hero:
// an animated green particle field, a soft green radial glow up top, a faint
// masked grid, and two blurred brand-colored blobs. Purely decorative, so it
// never intercepts pointers.
function Backdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div className="absolute inset-0">
        <HeroCanvas />
      </div>
      <div className="absolute inset-0 [background:radial-gradient(60%_50%_at_50%_-10%,rgba(97,206,112,0.16),transparent_70%)]" />
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:54px_54px] [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]" />
      <div className="absolute -top-24 -left-24 h-[30rem] w-[30rem] rounded-full bg-[#61CE70]/10 blur-[130px]" />
      <div className="absolute bottom-[-10%] right-[-5%] h-[34rem] w-[34rem] rounded-full bg-[#1E6BFF]/10 blur-[130px]" />
    </div>
  );
}

/* Typewriter — same rewriting-headline effect as the landing hero: types a
   phrase, holds, deletes, then types the next in a loop with a blinking
   caret. Reduced-motion users get the first phrase statically. Purely
   decorative, so it's aria-hidden (the h1 carries a stable sr-only heading
   for screen readers). */
function Typewriter({
  phrases,
  className = "",
  reduce = false,
}: {
  phrases: string[];
  className?: string;
  reduce?: boolean | null;
}) {
  const [text, setText] = useState(phrases[0]);
  const [index, setIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (reduce) return;
    const current = phrases[index % phrases.length];

    // Finished typing → hold, then start deleting.
    if (!deleting && text === current) {
      const id = window.setTimeout(() => setDeleting(true), 1700);
      return () => clearTimeout(id);
    }
    // Finished deleting → advance to the next phrase.
    if (deleting && text === "") {
      setDeleting(false);
      setIndex((v) => (v + 1) % phrases.length);
      return;
    }
    // Type or delete one character.
    const id = window.setTimeout(
      () =>
        setText(
          deleting
            ? current.slice(0, text.length - 1)
            : current.slice(0, text.length + 1),
        ),
      deleting ? 45 : 80,
    );
    return () => clearTimeout(id);
  }, [text, deleting, index, phrases, reduce]);

  return (
    <span aria-hidden="true">
      <span className={className}>{reduce ? phrases[0] : text}</span>
      <span className="ml-1 inline-block w-[0.05em] h-[0.82em] translate-y-[0.04em] rounded-[2px] bg-[#61CE70] align-middle animate-pulse" />
    </span>
  );
}

const Login: React.FC = () => {
  const { login, logout, user, status: authStatus } = useAuth();
  const { wallet, status: walletStatus } = useWallet();
  console.log(user);
  console.log(wallet);
  console.log(walletStatus);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Top-navigation state (scroll-aware header + mobile menu)
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Where to land after login. consumeReturnTo() clears the stored deep link
  // on first read, so the result is memoised for this mount: the checkUser
  // effect below can run more than once (StrictMode double-invoke, a wallet
  // status change while the first check is still awaiting), and a second,
  // empty read would otherwise replace the deep link with /dashboard.
  const returnToRef = useRef<string | null | undefined>(undefined);
  const postLoginPath = () => {
    if (returnToRef.current === undefined) returnToRef.current = consumeReturnTo();
    return returnToRef.current || "/dashboard";
  };

  // Reduced-motion preference — the typewriter falls back to a static
  // headline for these users, same as the landing hero. MUST be called
  // before the early-return loading/error states below, or the hook count
  // changes between renders ("Rendered fewer hooks than expected").
  const reduce = useReducedMotion();

  const walletAddress = wallet?.address?.toLowerCase() || null;
  const userEmail = user?.email || null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Send email via your backend
  const sendEmail = async (email: string, name: string, endpoint: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({ email, name }),
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.details || `API error: ${response.statusText}`,
        );
      }

      console.log(`${endpoint} email sent`);
    } catch (err) {
      console.error(`Failed to send ${endpoint} email:`, err);
      setError("Failed to send email. Please try again.");
    }
  };

  // Check user existence and handle redirect or onboarding
  useEffect(() => {
    // Only check auth status - wallet loads automatically via provider
    if (authStatus !== "logged-in" || !userEmail) {
      setShowOnboarding(false);
      return;
    }

    // Wait for wallet to load automatically
    if (walletStatus === "in-progress" || walletStatus === "not-loaded") {
      return; // Still loading, wait
    }

    if (walletStatus === "error") {
      setError("Wallet loading failed. Please try logging in again.");
      return;
    }

    // Wallet is now loaded successfully
    if (!walletAddress) {
      setError("Wallet loaded but no address found. Contact support.");
      return;
    }

    const checkUser = async () => {
      try {
        const q = query(
          collection(db, "users"),
          where("email", "==", userEmail),
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          const userData = userDoc.data();

          // Use wallet address as uid (since it's available now)
          const uid = walletAddress;

          dispatch(
            setUser({
              uid: uid,
              email: userEmail,
              phone: userData.phone || null,
              wallet: walletAddress,
              smartWallet: walletAddress,
              username: userData.username,
              joinedAt: userData.joinDate?.toDate?.().toISOString() || null,
              isBusiness: userData.isBusiness || false,
            }),
          );

          await sendEmail(userEmail, userData.username, "authenticate-user");

          // Record login activity
          try {
            await addDoc(collection(db, "loginActivity"), {
              walletAddress: walletAddress,
              email: userEmail,
              timestamp: serverTimestamp(),
            });
          } catch {}

          navigate(postLoginPath(), { replace: true });
        } else {
          setShowOnboarding(true);
        }
      } catch (err) {
        console.error("Error checking user:", err);
        setError("Error checking user. Please try again.");
      }
    };

    checkUser();
  }, [authStatus, userEmail, walletAddress, walletStatus, navigate, dispatch]);

  // Handle onboarding username submission
  const handleOnboarding = async () => {
    const cleanUsername = username.trim().toLowerCase();
    if (
      !cleanUsername ||
      cleanUsername.length < 6 ||
      cleanUsername.length > 12 ||
      /\s/.test(cleanUsername)
    ) {
      setError("Username must be 6–12 characters, no spaces.");
      return;
    }

    if (!userEmail || !walletAddress) {
      setError("Missing data. Please reconnect.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Check if username is taken
      const q = query(
        collection(db, "users"),
        where("username", "==", cleanUsername),
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setError("Username taken. Try another.");
        setLoading(false);
        return;
      }

      // Save new user using wallet address as document ID
      const userId = walletAddress;
      const userRef = doc(db, "users", userId);
      await setDoc(userRef, {
        username: cleanUsername,
        email: userEmail,
        phone: null,
        wallet: walletAddress,
        smartWallet: walletAddress,
        joinDate: serverTimestamp(),
      });

      dispatch(
        setUser({
          uid: userId,
          email: userEmail,
          phone: null,
          wallet: walletAddress,
          smartWallet: walletAddress,
          username: cleanUsername,
          joinedAt: new Date().toISOString(),
        }),
      );

      await sendEmail(userEmail, cleanUsername, "onboard-user");

      // Record login activity
      try {
        await addDoc(collection(db, "loginActivity"), {
          walletAddress: walletAddress,
          email: userEmail,
          timestamp: serverTimestamp(),
        });
      } catch {}

      navigate(postLoginPath(), { replace: true });
    } catch (err) {
      console.error("Onboarding error:", err);
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    dispatch(clearUser());
    await logout();
  };

  // Loading state while auth/wallet is initializing
  if (authStatus === "in-progress" || walletStatus === "in-progress") {
    return (
      <div className="relative min-h-dvh bg-[#07090D] flex items-center justify-center text-white overflow-hidden">
        <Backdrop />
        <div className="relative z-10 text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-white/10 border-t-[#61CE70]" />
          <p className="mt-4 text-lg text-gray-300">Loading…</p>
        </div>
      </div>
    );
  }

  // Handle wallet error state explicitly
  if (authStatus === "logged-in" && walletStatus === "error") {
    return (
      <div className="relative min-h-dvh bg-[#07090D] flex items-center justify-center text-white overflow-hidden px-4">
        <Backdrop />
        <div className="relative z-10 w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center backdrop-blur-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10 ring-1 ring-red-500/25">
            <X className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="mt-5 text-xl font-bold text-white">Wallet loading failed</h2>
          <p className="mt-2 text-sm text-gray-400">
            We couldn't load your wallet. Try signing in again.
          </p>
          <div className="mt-6 space-y-3">
            <button
              onClick={login}
              disabled={loading}
              className="w-full rounded-full bg-[#61CE70] px-6 py-3 font-bold text-[#06250f] transition-all hover:bg-[#6ed97d] active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? "Retrying…" : "Retry Login"}
            </button>
            <button
              onClick={handleLogout}
              className="w-full rounded-full border border-white/10 bg-white/[0.04] px-6 py-3 font-semibold text-white transition-all hover:bg-white/10 active:scale-[0.98]"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navLinks: { label: string; onClick: () => void }[] = [
    { label: "Developers", onClick: () => scrollTo("developers") },
    { label: "Policies", onClick: () => scrollTo("policies") },
    { label: "Docs", onClick: () => navigate("/developers/docs") },
  ];

  return (
    <div className="relative min-h-dvh bg-[#07090D] text-white overflow-x-hidden antialiased selection:bg-[#61CE70]/30">
      {/* ───── Top navigation ───── */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#07090D]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button onClick={() => navigate("/")} aria-label="CashTap home" className="flex items-center">
            <img src="/logo.png" alt="CashTap" className="h-14 sm:h-16 w-auto object-contain" />
          </button>

          <div className="hidden md:flex items-center gap-9 text-sm font-medium text-gray-300">
            {navLinks.map((l) => (
              <button key={l.label} onClick={l.onClick} className="hover:text-white transition-colors">
                {l.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2.5">
            <button
              onClick={() => navigate("/")}
              className="group inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-95"
              style={{ boxShadow: "0 10px 30px -10px rgba(97,206,112,0.7)" }}
            >
              <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
              Back to home
            </button>
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden p-2 rounded-lg text-gray-200 hover:bg-white/5"
            aria-label="Menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {menuOpen && (
          <div className="md:hidden bg-[#07090D]/95 backdrop-blur-xl border-b border-white/10 px-4 py-4 space-y-1">
            {navLinks.map((l) => (
              <button
                key={l.label}
                onClick={l.onClick}
                className="block w-full text-left px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-2">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/");
                }}
                className="flex w-full items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70]"
              >
                <ArrowLeft size={15} /> Back to home
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ───── Hero: brand story + auth card ───── */}
      <section id="top" className="relative flex min-h-dvh items-center pt-28 pb-16 sm:pb-20">
        <Backdrop />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-12">
            {/* left — brand copy + trust, mirroring the landing hero's
                staggered entrance and rewriting typewriter headline */}
            <div className="order-2 lg:order-1">
              <motion.div
                initial="hidden"
                animate="show"
                variants={fadeUp}
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-gray-300"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#61CE70] animate-pulse" />
                Welcome back to CashTap&nbsp;💚
              </motion.div>

              <motion.h1
                initial="hidden"
                animate="show"
                custom={1}
                variants={fadeUp}
                className="min-h-[2.4em] text-[2.4rem] font-extrabold leading-[1.08] tracking-tight sm:min-h-[2.3em] sm:text-5xl lg:text-[3.4rem]"
              >
                <span className="sr-only">
                  Securely manage your finances — connect your wallet, add
                  funds, and transfer assets with ease.
                </span>
                <Typewriter
                  reduce={reduce}
                  phrases={[
                    "Securely manage your finances.",
                    "Add funds in seconds.",
                    "Transfer assets with ease.",
                  ]}
                  className="bg-gradient-to-r from-[#61CE70] via-[#9BE8A6] to-[#61CE70] bg-clip-text text-transparent"
                />
              </motion.h1>

              <motion.p
                initial="hidden"
                animate="show"
                custom={2}
                variants={fadeUp}
                className="mt-5 max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg"
              >
                Connect your wallet, add funds, and transfer assets with ease. Our
                platform ensures top-notch security and seamless transactions.
              </motion.p>

              {/* trust chips — mirror the landing hero */}
              <motion.div
                initial="hidden"
                animate="show"
                custom={3}
                variants={fadeUp}
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
              >
                {[
                  { icon: ShieldCheck, label: "Licensed MSB (FinCEN)" },
                  { icon: Globe, label: "Global reach" },
                  { icon: Wallet, label: "You own your funds" },
                ].map((t) => (
                  <span key={t.label} className="flex items-center gap-2 text-xs text-gray-400 sm:text-[13px]">
                    <t.icon size={15} className="text-[#61CE70]" />
                    {t.label}
                  </span>
                ))}
              </motion.div>

              <motion.div
                initial="hidden"
                animate="show"
                custom={4}
                variants={fadeUp}
                className="mt-6 flex items-center gap-2 text-sm"
              >
                <span className="font-semibold text-[#9BE8A6]">Trusted by thousands</span>
                <span className="text-gray-600">·</span>
                <span className="text-gray-400">Secure &amp; Fast</span>
              </motion.div>
            </div>

            {/* right — auth card (all states preserved) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="order-1 lg:order-2"
            >
              <div className="relative mx-auto w-full max-w-md">
                <div className="pointer-events-none absolute -inset-4 rounded-[2.25rem] bg-[#61CE70]/10 blur-[50px]" />

                <div
                  className="relative overflow-hidden rounded-[28px] border border-white/10 p-7 sm:p-9"
                  style={{
                    background: "linear-gradient(160deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
                    boxShadow: "0 40px 90px -30px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,255,255,0.06)",
                    backdropFilter: "blur(12px)",
                  }}
                >
                  {/* top accent hairline */}
                  <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-[#61CE70]/60 to-transparent" />

                  {/* card brand lockup — centered, logo-forward */}
                  <div className="mb-7 flex justify-center">
                    <div className="relative">
                      <div className="pointer-events-none absolute -inset-6 rounded-full bg-[#61CE70]/15 blur-2xl" />
                      <img
                        src="/logo.png"
                        alt="CashTap"
                        className="relative h-12 w-auto object-contain sm:h-14"
                      />
                    </div>
                  </div>

                  {authStatus === "logged-in" ? (
                    <>
                      {showOnboarding ? (
                        <div className="space-y-5">
                          <div>
                            <h3 className="text-xl font-bold text-white">Complete your onboarding</h3>
                            <p className="mt-1.5 text-sm text-gray-400">
                              Choose a unique username (6–12 chars, no spaces).
                            </p>
                          </div>
                          <div>
                            <label htmlFor="username" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                              Username
                            </label>
                            <input
                              id="username"
                              type="text"
                              value={username}
                              onChange={(e) => setUsername(e.target.value)}
                              placeholder="Enter username"
                              maxLength={12}
                              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-gray-600 transition-all focus:border-[#61CE70]/50 focus:outline-none focus:ring-2 focus:ring-[#61CE70]/30"
                            />
                          </div>
                          {error && (
                            <p className="flex items-center gap-1.5 text-sm text-red-400">
                              <X size={14} /> {error}
                            </p>
                          )}
                          <button
                            onClick={handleOnboarding}
                            disabled={loading}
                            className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#61CE70] px-6 py-3.5 font-bold text-[#06250f] transition-all hover:bg-[#6ed97d] active:scale-[0.98] disabled:opacity-50"
                            style={{ boxShadow: "0 16px 40px -14px rgba(97,206,112,0.7)" }}
                          >
                            {loading ? "Saving…" : "Submit & continue"}
                            {!loading && <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-5 text-center">
                          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#61CE70]/15 ring-1 ring-[#61CE70]/25">
                            <Check className="h-7 w-7 text-[#61CE70]" strokeWidth={3} />
                          </div>
                          <h3 className="text-xl font-bold text-white">
                            Welcome, {userEmail || walletAddress?.slice(0, 10) + "…" || "User"}!
                          </h3>
                          <button
                            onClick={handleLogout}
                            className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#61CE70] px-6 py-3.5 font-bold text-[#06250f] transition-all hover:bg-[#6ed97d] active:scale-[0.98]"
                          >
                            <LogIn className="h-5 w-5 transition-transform group-hover:scale-110" />
                            <span>Log out</span>
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold tracking-tight text-white">
                          Sign in to continue
                        </h3>
                        <p className="mx-auto mt-2 max-w-[19rem] text-sm leading-relaxed text-gray-400">
                          Sign in with email or Google to get started.
                        </p>
                      </div>
                      <button
                        onClick={login}
                        className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#61CE70] px-6 py-4 text-base font-bold text-[#06250f] transition-all hover:bg-[#6ed97d] active:scale-[0.98]"
                        style={{ boxShadow: "0 16px 44px -14px rgba(97,206,112,0.75)" }}
                      >
                        <LogIn className="h-5 w-5 transition-transform group-hover:scale-110" />
                        <span>Sign in</span>
                        <ArrowRight
                          size={17}
                          className="transition-transform group-hover:translate-x-1"
                        />
                      </button>
                    </div>
                  )}

                  {/* terms microcopy — grounds the card now that the
                      compliance strip is gone; links jump to the policies
                      section further down the page */}
                  <p className="mt-6 text-center text-[11px] leading-relaxed text-gray-500">
                    By continuing, you agree to our{" "}
                    <button
                      type="button"
                      onClick={() => scrollTo("policies")}
                      className="text-gray-400 underline decoration-white/20 underline-offset-2 transition-colors hover:text-[#9BE8A6]"
                    >
                      Terms of Use
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      onClick={() => scrollTo("policies")}
                      className="text-gray-400 underline decoration-white/20 underline-offset-2 transition-colors hover:text-[#9BE8A6]"
                    >
                      Privacy Policy
                    </button>
                    .
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── For Developers — surfaces the API + Documentation story to
          prospective integrators before they sign up. */}
      <section id="developers" className="px-4 pb-12 sm:px-6 md:pb-20 lg:px-8" aria-labelledby="developers-heading">
        <div className="mx-auto max-w-7xl">
          <div className="border-t border-white/10 pt-8">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  <Terminal className="h-3.5 w-3.5 text-[#61CE70]" />
                  For Developers
                </p>
                <h2 id="developers-heading" className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Accept payments with a few lines of code.
                </h2>
                <p className="mt-2 max-w-xl text-sm text-gray-400">
                  A Stripe-shaped REST API, hosted checkout, signed webhooks, and
                  AI-friendly reference. Build once, accept every payment method on
                  CashTap.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <Link
                  to="/developers/docs"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#61CE70] px-4 py-2 text-sm font-semibold text-[#06250f] transition-colors hover:bg-[#6ed97d]"
                >
                  <BookOpen className="h-4 w-4" /> Read the docs
                </Link>
                <a
                  href="/llms.txt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-[#61CE70]/40 hover:text-white"
                >
                  <Bot className="h-4 w-4 text-[#61CE70]" /> llms.txt
                </a>
              </div>
            </div>

            {/* Feature cards — REST API, Webhooks, Docs, AI-ready. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <DeveloperCard
                icon={Code2}
                title="REST API"
                body="JSON over HTTPS. Bearer-token auth. Familiar shape if you've used Stripe Checkout."
                to="/developers/docs#create-session"
              />
              <DeveloperCard
                icon={Webhook}
                title="Webhooks"
                body="Signed events for payment success, expiry and failure. Automatic retries, delivery history and test sends."
                to="/developers/docs#webhooks"
              />
              <DeveloperCard
                icon={BookOpen}
                title="Documentation"
                body="Every endpoint, every field, every error code. With copy-paste samples in curl + JS."
                to="/developers/docs"
              />
              <DeveloperCard
                icon={Bot}
                title="AI-ready"
                body="Point Claude, Cursor, ChatGPT or Copilot at our llms.txt and they'll write the integration for you."
                to="/developers/docs#llms"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Policies — public legal pages anyone can read. */}
      <section id="policies" className="px-4 pb-12 sm:px-6 md:pb-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="border-t border-white/10 pt-8">
            <p className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              <ScrollText className="h-3.5 w-3.5 text-[#61CE70]" />
              Policies
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                {
                  to: "/aml-policy",
                  icon: ShieldCheck,
                  title: "AML & BSA Compliance",
                  subtitle: "Anti-money-laundering framework",
                },
                {
                  to: "/privacy-policy",
                  icon: Lock,
                  title: "Privacy & Data Policy",
                  subtitle: "How we handle your data",
                },
                {
                  to: "/terms-of-use",
                  icon: ScrollText,
                  title: "Terms of Use",
                  subtitle: "Customer & business terms",
                },
              ].map(({ to, icon: Icon, title, subtitle }) => (
                <Link
                  key={to}
                  to={to}
                  className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 transition-all hover:border-[#61CE70]/40 hover:bg-white/[0.04]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#61CE70]/20 bg-[#61CE70]/10 transition-colors group-hover:border-[#61CE70]/30 group-hover:bg-[#61CE70]/15">
                    <Icon className="h-5 w-5 text-[#61CE70]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-tight text-gray-100 transition-colors group-hover:text-white">
                      {title}
                    </span>
                    <span className="mt-1 block truncate text-[12px] leading-snug text-gray-500">
                      {subtitle}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-600 transition-all group-hover:translate-x-0.5 group-hover:text-[#61CE70]" />
                </Link>
              ))}
            </div>

            {/* bottom brand line */}
            <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-6 text-xs text-gray-500 sm:flex-row">
              <span>© {new Date().getFullYear()} CashTap. All rights reserved.</span>
              <span className="flex items-center gap-1.5">
                <Sparkles size={13} className="text-[#61CE70]" /> Secured with bank-grade encryption
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Card for the "For Developers" section. Visually rhymes with the policy
// cards (same hover affordance, same chevron) so the page bottom reads as a
// single coherent stripe of "stuff you can read before signing up":
// developer reference + legal docs.
function DeveloperCard({
  icon: Icon,
  title,
  body,
  to,
  badge,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  to: string;
  badge?: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 transition-all hover:border-[#61CE70]/40 hover:bg-white/[0.04]"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#61CE70]/20 bg-[#61CE70]/10 transition-colors group-hover:border-[#61CE70]/30 group-hover:bg-[#61CE70]/15">
          <Icon className="h-[18px] w-[18px] text-[#61CE70]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold leading-tight text-gray-100 transition-colors group-hover:text-white">
              {title}
            </p>
            {badge && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase leading-none tracking-wider text-amber-300">
                <span className="h-1 w-1 animate-pulse rounded-full bg-amber-400" />
                {badge}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-600 transition-all group-hover:translate-x-0.5 group-hover:text-[#61CE70]" />
      </div>
      <p className="text-[12.5px] leading-snug text-gray-500">{body}</p>
    </Link>
  );
}

export default Login;
